import { TextField, TextFieldProps } from '@rhg/libs/material';
import { forwardRef, ReactNode, Ref } from 'react';
import {
    Controller,
    ControllerProps,
    FieldValues,
    Path,
} from 'react-hook-form';

export type ValidatedTextFieldProps = TextFieldProps & {
    errorText?: string;
    label: string;
    name: string;
};

const ValidatedTextField = forwardRef(
    (
        {
            errorText,
            label,
            name,
            hiddenLabel,
            ...textFieldProps
        }: ValidatedTextFieldProps,
        ref: Ref<HTMLDivElement>
    ) => {
        const fieldErrorID = `${name}-field-error`;
        return (
            <TextField
                name={name}
                error={!!errorText}
                label={hiddenLabel ? undefined : label}
                helperText={errorText}
                FormHelperTextProps={{ id: fieldErrorID }}
                {...textFieldProps}
                InputProps={{
                    'aria-describedby': fieldErrorID,
                    ...textFieldProps.InputProps,
                }}
                inputProps={{
                    // only add the aria-label if labelledby isn't specified
                    ...(!textFieldProps.inputProps?.['aria-labelledby']
                        ? { 'aria-label': label }
                        : {}),
                    ...textFieldProps.inputProps,
                }}
                data-testid={`INPUT_${name}`}
                ref={ref}
            />
        );
    }
);

ValidatedTextField.displayName = 'ValidatedTextField';

export default ValidatedTextField;

type AdditionalControlledTextFieldProps = {
    textFieldProps?: Partial<ValidatedTextFieldProps>;
    label: string;
    required?: boolean;
    children?: ReactNode;
};

export function ControlledTextField<
    TFieldValues extends FieldValues = FieldValues,
    TName extends Path<TFieldValues> = Path<TFieldValues>
>({
    textFieldProps,
    label,
    required,
    children,
    ...controllerProps
}: Omit<ControllerProps<TFieldValues, TName>, 'render'> &
    AdditionalControlledTextFieldProps) {
    return (
        <Controller
            {...controllerProps}
            render={({ field, fieldState: { error } }) => (
                <ValidatedTextField
                    label={label}
                    {...textFieldProps}
                    {...field}
                    value={field.value ?? ''}
                    onChange={(...event) => {
                        field.onChange(...event);
                        textFieldProps?.onChange?.(...event);
                    }}
                    inputRef={field.ref}
                    required={required}
                    error={!!error?.message}
                    errorText={error?.message}
                >
                    {children}
                </ValidatedTextField>
            )}
        />
    );
}
